// mission-secrets-test-env-loader.ts — phase 04a (spec V2 §4.2, B17/B20/N33).
//
// `~/.avatar/secrets/<mission_id>/test.env` (HOME đọc lúc GỌI — test đổi HOME
// sang thư mục tạm, vitest-setup-strip-git-env.ts). Hai lối vào:
//  - `loadMissionTestEnv(missionId)`: đọc qua fd an toàn (FIFO/symlink/quá lớn ⇒
//    lỗi có cấu trúc, mission-secrets-test-env-safe-file-read.ts) + kiểm
//    quyền/owner trên fstat, trả kết quả có
//    cấu trúc (không ném vì quyền sai), và LUÔN hợp giá trị đọc được vào
//    registry (che thêm không bao giờ hại — kể cả khi quyền sai, N33). Preflight
//    `run` + doctor + (04b) merger env recipe dùng `status`; chỉ `ok` mới được
//    cấp cho recipe.
//  - `knownValuesFor(missionId)` (B20): getter của mọi sink — stat `test.env`
//    mỗi lần đọc; chữ ký (inode/size/mtime/mode/uid) khác lần trước hoặc file
//    mới xuất hiện ⇒ nạp lại trước khi trả. Đọc lazy lúc ghi/spawn, không chụp
//    lúc open (B17). Không bao giờ ném: sink không được chết vì file secrets.
import { promises as fs } from "node:fs";
import { homedir } from "node:os";
import { basename, dirname, join } from "node:path";
import {
  addKnownSecretKeyNames,
  addKnownSecretValues,
  recordTestEnvSignature,
  registeredKnownValues,
  testEnvSignatureFor,
} from "./mission-known-secret-values-registry.js";
import { isValidMissionId } from "./mission-resolver.js";
import {
  parseMissionTestEnvText,
  redactionFormsOfTestEnvValues,
  testEnvPermissionProblems,
} from "./mission-secrets-test-env-parser-and-permissions.js";
import { readTestEnvFileSafely } from "./mission-secrets-test-env-safe-file-read.js";

const TEST_ENV_FILE_NAME = "test.env";
const ABSENT_SIGNATURE = "absent";

export type MissionTestEnvLoadResult =
  | { status: "absent"; path: string }
  | {
      status: "ok";
      path: string;
      values: Record<string, string>;
      malformedLines: number[];
      rejectedKeys: string[];
    }
  | { status: "invalid_permissions"; path: string; problems: string[]; keys: string[] }
  | { status: "unreadable"; path: string; detail: string };

export interface LoadMissionTestEnvOptions {
  /** uid owner phải khớp — mặc định uid của process (account chạy `run`). */
  expectedUid?: number;
}

export function missionSecretsDir(missionId: string): string {
  return join(homedir(), ".avatar", "secrets", missionId);
}

export function missionTestEnvPath(missionId: string): string {
  return join(missionSecretsDir(missionId), TEST_ENV_FILE_NAME);
}

async function statSignature(path: string): Promise<string> {
  try {
    const s = await fs.lstat(path);
    return `${s.ino}:${s.size}:${s.mtimeMs}:${s.mode}:${s.uid}`;
  } catch {
    return ABSENT_SIGNATURE;
  }
}

export async function loadMissionTestEnv(
  missionId: string,
  options: LoadMissionTestEnvOptions = {},
): Promise<MissionTestEnvLoadResult> {
  if (!isValidMissionId(missionId)) return { status: "absent", path: "" };
  const dirPath = missionSecretsDir(missionId);
  const path = missionTestEnvPath(missionId);
  recordTestEnvSignature(missionId, await statSignature(path));
  const read = await readTestEnvFileSafely(dirPath, path);
  if (read.kind === "absent") return { status: "absent", path };
  if (read.kind === "error") return { status: "unreadable", path, detail: read.code };
  if (read.kind === "rejected") {
    return { status: "invalid_permissions", path, problems: read.problems, keys: [] };
  }
  // Chỉ file thường dưới trần mới tới đây — quyền sai vẫn nạp để che (N33).
  const parsed = parseMissionTestEnvText(read.text);
  addKnownSecretValues(missionId, "test_env", redactionFormsOfTestEnvValues(parsed.values));
  const expectedUid = options.expectedUid ?? process.getuid?.();
  const problems = testEnvPermissionProblems(dirPath, read.dir, path, read.file, expectedUid);
  if (problems.length > 0) {
    return { status: "invalid_permissions", path, problems, keys: Object.keys(parsed.values) };
  }
  // Codex F8: tên key chỉ thành token ranh giới khi nguồn đã kiểm chứng (`ok`).
  addKnownSecretKeyNames(missionId, Object.keys(parsed.values));
  return {
    status: "ok",
    path,
    values: parsed.values,
    malformedLines: parsed.malformedLines,
    rejectedKeys: parsed.rejectedKeys,
  };
}

/** Getter của mọi sink (B20): nạp lại khi `test.env` đổi/mới xuất hiện, rồi trả hợp registry. */
export async function knownValuesFor(missionId: string): Promise<string[]> {
  if (!isValidMissionId(missionId)) return [];
  try {
    const signature = await statSignature(missionTestEnvPath(missionId));
    if (signature !== testEnvSignatureFor(missionId)) {
      if (signature === ABSENT_SIGNATURE) recordTestEnvSignature(missionId, signature);
      else await loadMissionTestEnv(missionId);
    }
  } catch {
    // Sink không chết vì file secrets — giữ giá trị đã nạp (chỉ thêm, B21).
  }
  return registeredKnownValues(missionId);
}

/** `<workspace>/.avatar/missions/<id>/runtime` ⇒ `<id>`; layout khác ⇒ `undefined`. */
export function missionIdFromControlRoot(controlRoot: string): string | undefined {
  if (basename(controlRoot) !== "runtime") return undefined;
  const missionHome = dirname(controlRoot);
  if (basename(dirname(missionHome)) !== "missions") return undefined;
  const id = basename(missionHome);
  return isValidMissionId(id) ? id : undefined;
}

/** Getter cho sink chỉ có control root (commit, forensics, status, interview). */
export async function knownValuesForControlRoot(controlRoot: string): Promise<string[]> {
  const missionId = missionIdFromControlRoot(controlRoot);
  return missionId === undefined ? [] : knownValuesFor(missionId);
}
